import React, { useEffect, useState, useCallback } from "react";
import no_user_image from ".././images/no_user_image.svg";

// --- PendingLeaveApprovals ---------------------------------------------
// Web port of the mobile-app "LeaveApproval" screen. Lets a manager review
// and approve/reject their team's PENDING leave requests directly from the
// HR dashboard, without needing the mobile app.
//
// Talks to the exact same endpoints the mobile screen uses:
//   GET  /api/PersonalInfoLeaveApplicationList/{company}/PENDINGTEAM/{empId}
//   GET  /api/CpaysCount/{company}/AVAIL_LEAVES/{empId}/{category}
//   GET  /api/PersonalInfoLeaveStatusList/{company}/LEAVESTATUS/{empId}/{from}/{to}/-
//   GET  /api/PersonalInfoLeaveStatusList/{company}/LEAVEHISTORY/{empId}/{from}/{to}/-
//   GET  /api/PersonalInfoLeaveStatusList/{company}/LEAVEHISTORYOTHER/{empId}/{from}/{to}/-
//   POST /api/PersonalInfoLeaveStatusUpdate/{company}/{status}/{serialcode}/{approverId}/{ticketStatus}/{notes}
//
// Usage:
//   <PendingLeaveApprovals baseUrl={baseUrl} companyCode={company_code_from_local_storage} />
//   (loggedEmpId defaults to localStorage "currentUserEmployeeID" but can be overridden via prop)

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

// Matches the "dd-MMM-yyyy" format the API expects on the way in
function toApiDate(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${pad2(d.getDate())}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
}

// Display formatter, mirrors formatDateString() from the mobile screen
function toDisplayDate(dateStr, style = "yyyy-MM-dd") {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr).split("T")[0];
  if (style === "dd-MMM-yyyy") {
    return `${pad2(d.getDate())}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
  }
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function getProfilePicUrl(imagePath) {
  try {
    if (!imagePath) return null;
    const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "null");
    const imgBase = apiPaths?.[0]?.IMG_API_PATH;
    if (!imgBase) return null;
    const fileName = imagePath.replace(/\\/g, "/").split("/").pop();
    return `${imgBase}/BONDTIME_EMP_IMG/${fileName}`;
  } catch {
    return null;
  }
}

export default function PendingLeaveApprovals({
  baseUrl,
  companyCode,
  loggedEmpId,
}) {
  const approverId =
    loggedEmpId || localStorage.getItem("currentUserEmployeeID") || "";

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // --- Modal / review states ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [ticketStatus, setTicketStatus] = useState("Not Applicable");
  const [approvalNotes, setApprovalNotes] = useState("");
  const [availableDaysCount, setAvailableDaysCount] = useState(0);
  const [leaveStatusData, setLeaveStatusData] = useState([]);
  const [leaveHistoryData, setLeaveHistoryData] = useState([]);
  const [leaveHistoryOtherData, setLeaveHistoryOtherData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [popupApiError, setPopupApiError] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [imgError, setImgError] = useState(false);
  const [toast, setToast] = useState(null); // { type: "success" | "error", message }

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchPendingApprovals = useCallback(async () => {
    if (!baseUrl || !companyCode || !approverId) return;
    setIsLoading(true);
    setListError(null);
    try {
      const url = `${baseUrl}/api/PersonalInfoLeaveApplicationList/${companyCode}/PENDING/-`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to load pending leave requests");
      const json = await res.json();
      setData(Array.isArray(json) ? json : []);
    } catch (err) {
      console.error("Error fetching pending leave approvals:", err);
      setListError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, companyCode, approverId]);

  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  const handleOpenPopup = async (item) => {
    setSelectedItem(item);
    setTicketStatus("Not Applicable");
    setApprovalNotes("");
    setActiveTab(0);
    setImgError(false);
    setIsModalOpen(true);
    setModalLoading(true);
    setPopupApiError(false);

    setAvailableDaysCount(0);
    setLeaveStatusData([]);
    setLeaveHistoryData([]);
    setLeaveHistoryOtherData([]);

    const fromDateFormatted = toApiDate(item.FromDate);
    const toDateFormatted = toApiDate(item.ToDate);

    try {
      const encodedCategory = encodeURIComponent(item.Leave_Category);
      const daysRes = await fetch(
        `${baseUrl}/api/CpaysCount/${companyCode}/AVAIL_LEAVES/${item.EmpId}/${encodedCategory}`
      );
      const daysJson = await daysRes.json();
      setAvailableDaysCount(daysJson?.[0]?.Balance || 0);

      const statusBase = `${baseUrl}/api/PersonalInfoLeaveStatusList/${companyCode}`;
      const [otherHistory, currentStatus, empHistory] = await Promise.all([
        fetch(
          `${statusBase}/LEAVEHISTORYOTHER/${item.EmpId}/${fromDateFormatted}/${toDateFormatted}/-`
        ).then((r) => r.json()),
        fetch(
          `${statusBase}/LEAVESTATUS/${item.EmpId}/${fromDateFormatted}/${toDateFormatted}/-`
        ).then((r) => r.json()),
        fetch(
          `${statusBase}/LEAVEHISTORY/${item.EmpId}/${fromDateFormatted}/${toDateFormatted}/-`
        ).then((r) => r.json()),
      ]);

      setLeaveHistoryOtherData(Array.isArray(otherHistory) ? otherHistory : []);
      setLeaveStatusData(Array.isArray(currentStatus) ? currentStatus : []);
      setLeaveHistoryData(Array.isArray(empHistory) ? empHistory : []);
    } catch (err) {
      console.error("Error fetching popup dependencies:", err);
      setPopupApiError(true);
      setAvailableDaysCount(0);
      setLeaveStatusData([]);
      setLeaveHistoryData([]);
      setLeaveHistoryOtherData([]);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const postStatusUpdate = async (status) => {
    if (!approvalNotes || approvalNotes.trim() === "") {
      showToast(
        "error",
        `Notes are required to ${status === "APPROVED" ? "approve" : "reject"}.`
      );
      return;
    }

    try {
      setSubmitLoading(true);
      const userName = encodeURIComponent(approverId);
      const url = `${baseUrl}/api/PersonalInfoLeaveStatusUpdate/${companyCode}/${status}/${selectedItem?.serialcode}/${userName}/${ticketStatus}/${encodeURIComponent(
        approvalNotes
      )}`;

      const res = await fetch(url, { method: "POST" });
      if (!res.ok) throw new Error("Request failed");

      showToast(
        "success",
        status === "APPROVED"
          ? "Leave application approved."
          : "Leave application rejected."
      );

      setData((prev) =>
        prev.filter((item) => item.serialcode !== selectedItem.serialcode)
      );
      setIsModalOpen(false);
    } catch (err) {
      console.error("Status update error:", err);
      showToast("error", "Failed to update leave status. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const profilePicUrl =
    !imgError && selectedItem?.imagepath
      ? getProfilePicUrl(selectedItem.imagepath)
      : null;

  return (
    <div className="relative">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[60] px-4 py-2.5 rounded-lg shadow-lg text-xs font-semibold text-white ${
            toast.type === "success" ? "bg-emerald-600" : "bg-rose-600"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* --- Pending list --- */}
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-bold text-gray-700 tracking-wider uppercase">
          Pending Leave Approvals 
        </h3>
        {data.length > 0 && (
          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
            {data.length} pending
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400 gap-2 text-xs">
          <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <span>Loading pending requests...</span>
        </div>
      ) : listError ? (
        <p className="text-center text-xs text-rose-500 py-6 font-medium">
          Error loading requests: {listError}
        </p>
      ) : data.length === 0 ? (
        <p className="text-center text-xs text-gray-400 py-6">
          No pending leave requests for your team.
        </p>
      ) : (
        <div className="space-y-3">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto border border-gray-100 rounded-lg max-h-[400px] overflow-y-auto shadow-inner bg-gray-50/30">
            <table className="w-full text-left border-collapse text-xs relative">
              <thead className="sticky top-0 bg-gray-50 shadow-sm z-10">
                <tr className="border-b border-gray-100 text-gray-600 font-semibold">
                  <th className="p-3">Employee Details</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Date Range</th>
                  <th className="p-3">Days</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white text-gray-700">
                {data.map((item, index) => (
                  <tr
                    key={item.serialcode || index}
                    className="hover:bg-gray-50/60 transition-colors"
                  >
                    <td className="p-3">
                      <div className="font-semibold text-gray-900">
                        {item.Name?.trim()}
                      </div>
                      <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                        ID: {item.EmpId} | {item.JobTitle || "Staff"}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="bg-amber-50 text-amber-700 border border-amber-100/70 px-1.5 py-0.5 rounded font-medium text-[11px]">
                        {item.Leave_Category}
                      </span>
                    </td>
                    <td className="p-3 font-medium">
                      {toDisplayDate(item.FromDate)} to{" "}
                      {toDisplayDate(item.ToDate)}
                    </td>
                    <td className="p-3 font-semibold text-teal-700">
                      {item.Days} {item.Days === 1 ? "Day" : "Days"}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => handleOpenPopup(item)}
                        className="bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-semibold px-3 py-1.5 rounded-md transition-colors cursor-pointer"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="grid grid-cols-1 gap-3 md:hidden max-h-[420px] overflow-y-auto p-1">
            {data.map((item, index) => (
              <div
                key={item.serialcode || index}
                className="bg-white border border-gray-100 rounded-lg p-3 text-xs space-y-2 shadow-xs"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h4 className="font-bold text-gray-900 truncate">
                      {item.Name?.trim()}
                    </h4>
                    <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                      ID: {item.EmpId} • {item.JobTitle || "Staff"}
                    </p>
                  </div>
                  <span className="bg-amber-50 text-amber-700 border border-amber-100/70 px-1.5 py-0.5 rounded font-medium text-[10px] shrink-0">
                    {item.Leave_Category}
                  </span>
                </div>

                <div className="flex items-center justify-between bg-gray-50/80 p-2 rounded border border-gray-100/50">
                  <span className="text-[11px] font-medium text-gray-600">
                    {toDisplayDate(item.FromDate)} → {toDisplayDate(item.ToDate)}
                  </span>
                  <span className="text-[11px] font-bold text-teal-700">
                    {item.Days} {item.Days === 1 ? "Day" : "Days"}
                  </span>
                </div>

                <button
                  onClick={() => handleOpenPopup(item)}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-semibold py-2 rounded-md transition-colors cursor-pointer"
                >
                  Review
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* --- Review modal --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/40 backdrop-blur-xs transition-opacity">
          <div className="absolute inset-0" onClick={handleCloseModal} />
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[75vh] flex flex-col z-10 relative border border-gray-100">
            {/* Header */}
            <div className="px-4 py-3.5 bg-indigo-600 flex items-center justify-between shrink-0">
              <h3 className="text-sm font-bold text-white">
                Leave Approval Review
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-white/80 hover:text-white p-1 rounded-lg cursor-pointer"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {modalLoading ? (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-400 text-xs">
                  <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  <span>Loading employee details...</span>
                </div>
              ) : popupApiError ? (
                <div className="p-7 flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-rose-50 border-2 border-rose-100 flex items-center justify-center mb-4">
                    <span className="text-2xl font-extrabold text-rose-600">
                      !
                    </span>
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-2">
                    Failed to Load Details
                  </h4>
                  <p className="text-xs text-gray-500 mb-5 leading-relaxed">
                    Could not fetch data for{" "}
                    <span className="font-bold text-gray-900">
                      {selectedItem?.Name}
                    </span>
                    . The server returned an error or could not be reached.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 mb-6">
                    <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 min-w-[80px] text-center">
                      <div className="text-[9px] font-bold text-gray-400 tracking-wide">
                        EMP ID
                      </div>
                      <div className="text-xs font-bold text-gray-700">
                        {selectedItem?.EmpId}
                      </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 min-w-[80px] text-center">
                      <div className="text-[9px] font-bold text-gray-400 tracking-wide">
                        LEAVE TYPE
                      </div>
                      <div className="text-xs font-bold text-gray-700">
                        {selectedItem?.Leave_Category}
                      </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 min-w-[80px] text-center">
                      <div className="text-[9px] font-bold text-gray-400 tracking-wide">
                        DAYS
                      </div>
                      <div className="text-xs font-bold text-gray-700">
                        {selectedItem?.Days}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleOpenPopup(selectedItem)}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold py-3 rounded-lg mb-2.5 cursor-pointer"
                  >
                    ↺ Retry
                  </button>
                  <button
                    onClick={handleCloseModal}
                    className="w-full border border-gray-200 text-gray-600 text-sm font-semibold py-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {/* Profile card */}
                  <div className="flex flex-col sm:flex-row border border-gray-200 rounded-lg overflow-hidden bg-gray-50/60">
                    <div className="flex-1 p-3.5">
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={profilePicUrl || no_user_image}
                          onError={() => setImgError(true)}
                          alt="Profile"
                          className="w-11 h-11 rounded-full object-cover bg-gray-200 shrink-0"
                        />
                        <div className="min-w-0">
                          <div className="font-bold text-gray-900 text-sm truncate">
                            {selectedItem?.Name}
                          </div>
                          <span className="inline-block mt-1 bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase">
                            {selectedItem?.ApprovalStatus || "Pending"}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 pt-2.5 border-t border-gray-200/60">
                        <div>
                          <div className="text-[8.5px] font-bold text-gray-400 tracking-wide mb-0.5">
                            EMPLOYEE ID
                          </div>
                          <div className="text-[11.5px] font-semibold text-gray-600">
                            {selectedItem?.EmpId}
                          </div>
                        </div>
                        <div>
                          <div className="text-[8.5px] font-bold text-gray-400 tracking-wide mb-0.5">
                            FROM DATE
                          </div>
                          <div className="text-[11.5px] font-semibold text-gray-600">
                            {toDisplayDate(selectedItem?.FromDate)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[8.5px] font-bold text-gray-400 tracking-wide mb-0.5">
                            TO DATE
                          </div>
                          <div className="text-[11.5px] font-semibold text-gray-600">
                            {toDisplayDate(selectedItem?.ToDate)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex sm:flex-col gap-2 p-2.5 bg-gray-100/70 sm:w-[92px] sm:border-l border-t sm:border-t-0 border-gray-200 justify-center">
                      <div className="flex-1 sm:flex-none bg-white rounded-md border border-gray-200 py-1.5 text-center">
                        <div className="text-sm font-extrabold text-indigo-600">
                          {selectedItem?.Days}
                        </div>
                        <div className="text-[8.5px] font-semibold text-gray-500">
                          Requested
                        </div>
                      </div>
                      <div className="flex-1 sm:flex-none bg-emerald-50 rounded-md border border-emerald-100 py-1.5 text-center">
                        <div className="text-sm font-extrabold text-emerald-700">
                          {availableDaysCount}
                        </div>
                        <div className="text-[8.5px] font-semibold text-emerald-700/80">
                          Available
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Flight ticket + notes */}
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                      Flight Ticket
                    </label>
                    <select
                      value={ticketStatus}
                      onChange={(e) => setTicketStatus(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2.5 py-2 text-xs text-gray-800 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
                    >
                      <option value="Not Applicable">Not Applicable</option>
                      <option value="One Way">One Way</option>
                      <option value="Two Way">Two Way</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                      Notes *
                    </label>
                    <textarea
                      value={approvalNotes}
                      onChange={(e) => setApprovalNotes(e.target.value)}
                      rows={3}
                      placeholder="Enter evaluation notes here..."
                      className="w-full border border-gray-300 rounded-md p-2.5 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                    />
                  </div>

                  {/* Approve / Reject */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      disabled={submitLoading}
                      onClick={() => postStatusUpdate("APPROVED")}
                      className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold text-sm py-2.5 rounded-md transition-colors cursor-pointer disabled:cursor-not-allowed"
                    >
                      {submitLoading ? "Please wait..." : "Approve"}
                    </button>
                    <button
                      disabled={submitLoading}
                      onClick={() => postStatusUpdate("REJECTED")}
                      className="bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white font-bold text-sm py-2.5 rounded-md transition-colors cursor-pointer disabled:cursor-not-allowed"
                    >
                      {submitLoading ? "Please wait..." : "Reject"}
                    </button>
                  </div>

                  {/* Tab bar */}
                  <div className="flex flex-col md:flex-row bg-slate-100 rounded-lg p-1 gap-1">
                    {["Current Status", "History", "Other"].map((label, idx) => (
                      <button
                        key={label}
                        onClick={() => setActiveTab(idx)}
                        className={`flex-1 py-2 rounded-md text-[11px] font-semibold cursor-pointer transition-colors ${
                          activeTab === idx
                            ? "bg-white text-indigo-600 shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Tab content */}
                  {activeTab === 0 && (
                    <div>
                      <div className="text-[11px] font-bold text-slate-500 mb-2">
                        Current Leave Status Of Employee
                      </div>
                      <div className="overflow-x-auto border border-gray-200 rounded-md">
                        <table className="w-full text-[11px] text-center border-collapse min-w-[420px]">
                          <thead>
                            <tr className="bg-indigo-600 text-white">
                              <th className="p-2 text-left font-semibold">
                                Leave Category
                              </th>
                              <th className="p-2 font-semibold">Alloted</th>
                              <th className="p-2 font-semibold">Consumed</th>
                              <th className="p-2 font-semibold">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaveStatusData.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="p-5 text-gray-400 italic text-xs"
                                >
                                  No leave status records found.
                                </td>
                              </tr>
                            ) : (
                              leaveStatusData.map((d, i) => (
                                <tr key={i} className="border-b border-gray-100 bg-white">
                                  <td className="p-2 text-left">
                                    {d?.LEAVECATEGORY}
                                  </td>
                                  <td className="p-2">{d?.ALLOTED}</td>
                                  <td className="p-2">{d?.CONSUMED}</td>
                                  <td className="p-2">{d?.Balance}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {activeTab === 1 && (
                    <div>
                      <div className="text-[11px] font-bold text-slate-500 mb-2">
                        Leave History of Employee
                      </div>
                      <div className="overflow-x-auto border border-gray-200 rounded-md">
                        <table className="w-full text-[11px] text-center border-collapse min-w-[480px]">
                          <thead>
                            <tr className="bg-indigo-600 text-white">
                              <th className="p-2 font-semibold">Type</th>
                              <th className="p-2 font-semibold">From</th>
                              <th className="p-2 font-semibold">To</th>
                              <th className="p-2 font-semibold">Leaves</th>
                              <th className="p-2 font-semibold">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaveHistoryData.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="p-5 text-gray-400 italic text-xs"
                                >
                                  No leave history found.
                                </td>
                              </tr>
                            ) : (
                              leaveHistoryData.map((d, i) => (
                                <tr key={i} className="border-b border-gray-100 bg-white">
                                  <td className="p-2">{d?.Leave_Type}</td>
                                  <td className="p-2">
                                    {toDisplayDate(d?.FROM_DT)}
                                  </td>
                                  <td className="p-2">
                                    {toDisplayDate(d?.TO_DATE)}
                                  </td>
                                  <td className="p-2">{d?.LEAVES}</td>
                                  <td className="p-2">{d?.Status}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {activeTab === 2 && (
                    <div>
                      <div className="text-[11px] font-bold text-slate-500 mb-2">
                        Other Employees Approved Leaves in the Selected Period
                      </div>
                      <div className="overflow-x-auto border border-gray-200 rounded-md">
                        <table className="w-full text-[11px] text-center border-collapse min-w-[560px]">
                          <thead>
                            <tr className="bg-indigo-600 text-white">
                              <th className="p-2 font-semibold">Emp Id</th>
                              <th className="p-2 text-left font-semibold">Name</th>
                              <th className="p-2 text-left font-semibold">
                                Job Title
                              </th>
                              <th className="p-2 font-semibold">Leave Name</th>
                              <th className="p-2 font-semibold">From</th>
                              <th className="p-2 font-semibold">To</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaveHistoryOtherData.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="p-5 text-gray-400 italic text-xs"
                                >
                                  No other employee records found for this
                                  duration.
                                </td>
                              </tr>
                            ) : (
                              leaveHistoryOtherData.map((d, i) => (
                                <tr key={i} className="border-b border-gray-100 bg-white">
                                  <td className="p-2">{d?.EmpId}</td>
                                  <td className="p-2 text-left">{d?.Name}</td>
                                  <td className="p-2 text-left">
                                    {d?.JobTitle}
                                  </td>
                                  <td className="p-2">{d?.Leave_Name}</td>
                                  <td className="p-2">
                                    {toDisplayDate(d?.Leave_From)}
                                  </td>
                                  <td className="p-2">
                                    {toDisplayDate(d?.Leave_To)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}